import type { TemporalSnapshotCut } from "./temporalState";
import { abbreviatedSnapshotToken } from "./temporalState";

export function TemporalLineageControl({
  effectiveInput,
  normalizedEffectiveAsOf,
  validationError,
  cut,
  isFreezing,
  supportsTemporalReplay = true,
  onEffectiveInput,
  onApply,
  onUseNow,
}: {
  effectiveInput: string;
  normalizedEffectiveAsOf: string | null;
  validationError: string | null;
  cut: TemporalSnapshotCut | null;
  isFreezing: boolean;
  supportsTemporalReplay?: boolean;
  onEffectiveInput: (value: string) => void;
  onApply: () => void;
  onUseNow: () => void;
}) {
  const frozen = cut !== null
    && cut.effectiveAsOf === normalizedEffectiveAsOf
    && validationError === null;
  return (
    <section
      className={`dag-temporal-cut${validationError ? " is-invalid" : ""}`}
      aria-label="Temporal lineage cut"
      data-testid="dag-temporal-cut"
    >
      <form className="dag-temporal-cut__effective" onSubmit={(event) => { event.preventDefault(); onApply(); }}>
        <label htmlFor="dag-effective-as-of">
          <span>Effective / event time</span>
          <input
            id="dag-effective-as-of"
            type="text"
            autoComplete="off"
            spellCheck={false}
            inputMode="text"
            value={effectiveInput}
            aria-invalid={validationError ? "true" : "false"}
            aria-describedby={validationError ? "dag-effective-as-of-error" : "dag-effective-as-of-help"}
            placeholder="Server now"
            onChange={(event) => onEffectiveInput(event.target.value)}
          />
        </label>
        <button type="submit" disabled={validationError !== null || normalizedEffectiveAsOf === null}>Apply</button>
        <button type="button" onClick={onUseNow}>Now</button>
      </form>
      <div className="dag-temporal-cut__system" aria-live="polite">
        <span>Recorded / system time</span>
        <strong title={cut?.recordedAsOf}>{cut?.recordedAsOf ?? (
          supportsTemporalReplay ? (isFreezing ? "Freezing…" : "Not frozen") : "Current-only source"
        )}</strong>
      </div>
      <div className="dag-temporal-cut__token">
        <span>Snapshot token</span>
        <code title={cut?.snapshotToken}>{cut ? abbreviatedSnapshotToken(cut.snapshotToken) : supportsTemporalReplay ? "—" : "Unavailable"}</code>
      </div>
      <output className={`dag-temporal-cut__state${frozen ? " is-frozen" : ""}`}>
        <i aria-hidden="true" />
        {validationError
          ? "Invalid cut"
          : frozen
            ? "Frozen"
            : supportsTemporalReplay
              ? isFreezing ? "Freezing" : "Awaiting cut"
              : "Current only"}
      </output>
      {validationError ? (
        <p id="dag-effective-as-of-error" role="alert">{validationError} Requests are paused.</p>
      ) : (
        <p id="dag-effective-as-of-help">
          {supportsTemporalReplay
            ? "RFC 3339 input is normalized to an exact UTC deep link."
            : "This compatibility source has no recorded/system history. Historical input pauses the surface."}
        </p>
      )}
    </section>
  );
}
